import {
  Container,
  SpaceBetween,
  Button,
  Header,
  Box,
} from "@amzn/awsui-components-react/polaris";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { setSkipToStep } from "../../components/wizard/wizardSlice";
import React from "react";
import { ReviewPageContentsProps } from "../reviewPage/ReviewPage";
import {
  CLAIM_VALIDATION_CONSTANTS,
  PRODUCT_INFORMATION_CONSTANTS,
  CLAIM_INFORMATION_CONSTANTS,
  ADDITIONAL_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
} from "./constants";

interface StepContentsProps {
  stepHeader: string;
  stepNumber: number;
  content: React.ReactElement;
}

const StepContents = ({
  stepHeader,
  stepNumber,
  content,
}: StepContentsProps) => {
  const dispatch = useAppDispatch();

  const setActiveStepIndex = (step: number) => {
    dispatch(setSkipToStep({ value: step }));
  };

  return (
    <SpaceBetween size="xs">
      <Header
        variant="h3"
        headingTagOverride="h2"
        actions={
          <Button
            onClick={() => setActiveStepIndex(stepNumber)}
            ariaLabel={`Edit, press this button to go back to Step ${
              stepNumber + 1
            }: ${stepHeader}`}
          >
            Edit
          </Button>
        }
      >
        {`Step ${stepNumber + 1}`}
      </Header>
      <Container
        header={
          <Header variant="h2" headingTagOverride="h3">
            {stepHeader}
          </Header>
        }
      >
        {content}
      </Container>
    </SpaceBetween>
  );
};

const IPLReviewPageContents = ({
  allFormConfigs,
  renderFieldGroup,
  renderFileList,
}: ReviewPageContentsProps) => {
  const { allFormStates } = useAppSelector((state: RootState) => state.forms);

  const claimValidationFields =
    allFormConfigs[CLAIM_VALIDATION_CONSTANTS.FORM_NAME]?.[0]?.fields ?? [];
  const productInformationFields =
    allFormConfigs[PRODUCT_INFORMATION_CONSTANTS.FORM_NAME]?.[0]?.fields ?? [];
  const claimInformationFields =
    allFormConfigs[CLAIM_INFORMATION_CONSTANTS.FORM_NAME]?.[0]?.fields ?? [];
  const additionalInformationFields =
    allFormConfigs[ADDITIONAL_INFORMATION_CONSTANTS.FORM_NAME]?.[0]?.fields ??
    [];
  const fileAttachmentsFields =
    allFormConfigs[FILE_ATTACHMENTS_CONSTANTS.FORM_NAME]?.[0]?.fields ?? [];

  return (
    <Box margin={{ bottom: "l" }}>
      <SpaceBetween size="xxl">
        <StepContents
          stepHeader={CLAIM_VALIDATION_CONSTANTS.FORM_HEADER}
          stepNumber={0}
          content={
            <SpaceBetween size="m">
              {renderFieldGroup(
                "",
                claimValidationFields,
                allFormStates[CLAIM_VALIDATION_CONSTANTS.FORM_NAME],
                CLAIM_VALIDATION_CONSTANTS.FORM_NAME
              )}
            </SpaceBetween>
          }
        />
        <StepContents
          stepHeader={PRODUCT_INFORMATION_CONSTANTS.FORM_HEADER}
          stepNumber={1}
          content={
            <SpaceBetween size="m">
              {renderFieldGroup(
                "",
                productInformationFields,
                allFormStates[PRODUCT_INFORMATION_CONSTANTS.FORM_NAME],
                PRODUCT_INFORMATION_CONSTANTS.FORM_NAME
              )}
            </SpaceBetween>
          }
        />
        <StepContents
          stepHeader={CLAIM_INFORMATION_CONSTANTS.FORM_HEADER}
          stepNumber={2}
          content={
            <SpaceBetween size="m">
              {renderFieldGroup(
                "",
                claimInformationFields,
                allFormStates[CLAIM_INFORMATION_CONSTANTS.FORM_NAME],
                CLAIM_INFORMATION_CONSTANTS.FORM_NAME
              )}
            </SpaceBetween>
          }
        />
        <StepContents
          stepHeader={ADDITIONAL_INFORMATION_CONSTANTS.FORM_HEADER}
          stepNumber={3}
          content={
            <SpaceBetween size="m">
              {renderFieldGroup(
                "",
                additionalInformationFields,
                allFormStates[ADDITIONAL_INFORMATION_CONSTANTS.FORM_NAME],
                ADDITIONAL_INFORMATION_CONSTANTS.FORM_NAME
              )}
            </SpaceBetween>
          }
        />
        <StepContents
          stepHeader={FILE_ATTACHMENTS_CONSTANTS.FORM_HEADER}
          stepNumber={4}
          content={
            <SpaceBetween size="m">
              {allFormStates[FILE_ATTACHMENTS_CONSTANTS.FORM_NAME] ? renderFileList(allFormStates[FILE_ATTACHMENTS_CONSTANTS.FORM_NAME], fileAttachmentsFields) : <div>No file attachments</div>}
            </SpaceBetween>
          }
        />
      </SpaceBetween>
    </Box>
  );
};

export default IPLReviewPageContents;
